import { useState, useEffect } from 'react';
import { useVideoSession } from '@/contexts/VideoSessionContext';
import { retrieveVideoFile } from '@/utils/videoIndexedDb';
import { useMediaUrl } from '@/hooks/useMediaUrl';

export type VideoSourceType = 'blob' | 'indexeddb' | 'server' | 'none';

interface VideoSourceResult {
  url: string | null;
  sourceType: VideoSourceType;
}

/**
 * 4-level fallback hook for resolving a video URL.
 *
 * For athlete videos (ephemeral):
 *   1. In-memory Blob URL from VideoSessionContext (uploaded this session)
 *   2. IndexedDB file → create Blob URL (survived page reload, within 24h TTL)
 *   3. Server streaming URL (always attempted — works for existing dev videos,
 *      premium-saved videos, and admin "Use Existing" flows)
 *   4. null / 'none' — only if server 404s AND no local source exists
 *
 * For template videos: always return the server URL directly — templates are permanent.
 *
 * IMPORTANT: The server URL is set as the INITIAL state so the video element
 * always has a src on first render. Blob/IndexedDB sources upgrade from there.
 * This avoids the flash-of-null that breaks video playback and overlays.
 *
 * Media-auth security sprint (2026-09-21, MEDIA_AUTH_CONTRACT.md): the server
 * level's URL is now a short-lived SIGNED URL minted via `useMediaUrl`, not
 * the raw unsigned `/videos/{id}/stream` URL — the endpoint 401s without a
 * bearer header or a valid `sig`/`exp` pair, and a bare `<video src>` sends
 * neither. While the token is loading, the server level's `url` is `null`
 * (never falls back to the unsigned URL) — the fallback ORDER (blob →
 * indexeddb → server) is otherwise unchanged.
 */
export function useVideoSource(
  videoId: string | undefined,
  videoType: 'athlete' | 'template' = 'athlete'
): VideoSourceResult {
  const { getBlobUrl } = useVideoSession();
  const { streamUrl: signedServerUrl } = useMediaUrl(videoId);

  // Resolve synchronously: Blob URL first, then the signed server URL (or
  // null while the token is still loading) as default. This ensures the
  // video element never gets an unsigned src, even on the initial render.
  const initialResult = (): VideoSourceResult => {
    if (!videoId) return { url: null, sourceType: 'none' };

    // Level 1: In-memory Blob URL (fastest, zero-copy)
    const blobUrl = getBlobUrl(videoId);
    if (blobUrl) return { url: blobUrl, sourceType: 'blob' };

    // Default: signed server URL (works for templates, existing dev videos,
    // and saved videos) — null while the media token is loading.
    return { url: signedServerUrl, sourceType: 'server' };
  };

  const [result, setResult] = useState<VideoSourceResult>(initialResult);

  // Keep the 'server' level's URL in sync as the signed token resolves/refreshes.
  // Blob/IndexedDB levels take priority and are handled by the effect below.
  useEffect(() => {
    setResult((prev) => {
      if (prev.sourceType !== 'server') return prev;
      if (prev.url === signedServerUrl) return prev;
      return { url: signedServerUrl, sourceType: 'server' };
    });
  }, [signedServerUrl]);

  useEffect(() => {
    if (!videoId) {
      setResult({ url: null, sourceType: 'none' });
      return;
    }

    // Level 1: Check Blob URL (synchronous, already checked in initialResult
    // but re-check in case videoId changed)
    const blobUrl = getBlobUrl(videoId);
    if (blobUrl) {
      setResult({ url: blobUrl, sourceType: 'blob' });
      return;
    }

    // For templates, always use the signed server URL — no IndexedDB check needed
    if (videoType === 'template') {
      setResult({ url: signedServerUrl, sourceType: 'server' });
      return;
    }

    // Level 2: Check IndexedDB for athlete videos (async)
    // Meanwhile, the signed server URL (or null while loading) from initial
    // state keeps the video playable once the token resolves.
    let cancelled = false;
    let createdBlobUrl: string | null = null;

    (async () => {
      try {
        const file = await retrieveVideoFile(videoId);
        if (cancelled) return;

        if (file) {
          createdBlobUrl = URL.createObjectURL(file);
          setResult({ url: createdBlobUrl, sourceType: 'indexeddb' });
          return;
        }
      } catch {
        // IndexedDB unavailable — keep server URL from initial state
      }

      // If no IndexedDB hit, the server URL from initial state is already set
      // (and kept fresh by the effect above). No need to update here.
    })();

    return () => {
      cancelled = true;
      if (createdBlobUrl) {
        try {
          URL.revokeObjectURL(createdBlobUrl);
        } catch {
          // Ignore
        }
      }
    };
    // signedServerUrl is intentionally excluded below: it's handled by the
    // dedicated effect above so this effect (blob/indexeddb resolution)
    // doesn't re-run on every token refresh.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [videoId, videoType, getBlobUrl]);

  return result;
}
